import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("reports");
  const [reports, setReports] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [totalUsers, setTotalUsers] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/reports");
      setReports(response.data || []);
    } catch {
      toast.error("Unable to load reports.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBlockedUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/blocked-users");
      setBlockedUsers(response.data || []);
    } catch {
      toast.error("Unable to load blocked users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    setSelectedIds([]);
    if (tab === "reports") fetchReports();
    else fetchBlockedUsers();
  }, [user, tab, fetchReports, fetchBlockedUsers]);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/stats").then((r) => setTotalUsers(r.data.total_users)).catch(() => {});
  }, [user]);

  const toggleSelect = (pollId) => {
    setSelectedIds((prev) =>
      prev.includes(pollId) ? prev.filter((id) => id !== pollId) : [...prev, pollId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === reports.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(reports.map((r) => r.poll_id));
    }
  };

  const executeBulk = async () => {
    if (!bulkAction || selectedIds.length === 0) return;
    setProcessing(true);
    try {
      if (bulkAction === "delete") {
        for (const id of selectedIds) {
          await api.delete(`/admin/polls/${id}`);
        }
        toast.success(`${selectedIds.length} poll(s) deleted.`);
      } else if (bulkAction === "ignore") {
        for (const id of selectedIds) {
          await api.post(`/admin/reports/${id}/ignore`);
        }
        toast.success(`${selectedIds.length} report(s) ignored.`);
      } else if (bulkAction === "block") {
        const userIds = [
          ...new Set(
            reports
              .filter((r) => selectedIds.includes(r.poll_id) && r.poll_creator_user_id)
              .map((r) => r.poll_creator_user_id)
          ),
        ];
        for (const uid of userIds) {
          await api.post(`/admin/users/${uid}/block`);
        }
        toast.success(`${userIds.length} user(s) blocked.`);
      }
      setSelectedIds([]);
      fetchReports();
    } catch {
      toast.error("Some actions failed. Refresh to see current state.");
    } finally {
      setProcessing(false);
      setBulkAction(null);
    }
  };

  const handleUnblock = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/unblock`);
      setBlockedUsers((prev) => prev.filter((u) => u.id !== userId));
      toast.success("User unblocked.");
    } catch {
      toast.error("Unable to unblock user.");
    }
  };

  if (!user) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="admin-unauthenticated">
        Sign in to access admin tools.
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="admin-forbidden">
        Admin access required.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <h2 className="text-2xl font-semibold" data-testid="admin-title">Admin</h2>

      {totalUsers !== null && (
        <div
          className="rounded-xl border border-border bg-card px-4 py-3 text-sm"
          data-testid="admin-total-users"
        >
          Total Users: <span className="font-semibold">{totalUsers}</span>
        </div>
      )}

      <div className="flex gap-2 border-b border-border" data-testid="admin-tabs">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === "reports" ? "border-primary text-primary" : "border-transparent text-muted-foreground"
          }`}
          onClick={() => setTab("reports")}
          data-testid="admin-tab-reports"
        >
          Reports
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === "blocked" ? "border-primary text-primary" : "border-transparent text-muted-foreground"
          }`}
          onClick={() => setTab("blocked")}
          data-testid="admin-tab-blocked"
        >
          Blocked Users
        </button>
      </div>

      {tab === "reports" && (
        <>
          {selectedIds.length > 0 && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3"
              data-testid="admin-bulk-bar"
            >
              <span className="text-sm font-medium" data-testid="admin-bulk-count">
                {selectedIds.length} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkAction("delete")}
                disabled={processing}
                data-testid="admin-bulk-delete"
              >
                Delete
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkAction("ignore")}
                disabled={processing}
                data-testid="admin-bulk-ignore"
              >
                Ignore
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkAction("block")}
                disabled={processing}
                data-testid="admin-bulk-block"
              >
                Block User
              </Button>
              {processing && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
          )}

          {loading ? (
            <div className="text-sm text-muted-foreground" data-testid="admin-loading">
              Loading reports...
            </div>
          ) : reports.length === 0 ? (
            <div
              className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground"
              data-testid="admin-empty"
            >
              No reports yet.
            </div>
          ) : (
            <div className="overflow-auto" data-testid="admin-table">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3 w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === reports.length && reports.length > 0}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-border"
                        data-testid="admin-select-all"
                      />
                    </th>
                    <th className="py-2">Poll</th>
                    <th className="py-2">Creator</th>
                    <th className="py-2">Reports</th>
                    <th className="py-2">Latest</th>
                    <th className="py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr
                      key={report.poll_id}
                      className="border-t border-border"
                      data-testid={`admin-row-${report.poll_id}`}
                    >
                      <td className="py-3 pr-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(report.poll_id)}
                          onChange={() => toggleSelect(report.poll_id)}
                          className="h-4 w-4 rounded border-border"
                          data-testid={`admin-select-${report.poll_id}`}
                        />
                      </td>
                      <td className="py-3 pr-4">
                        <div className="font-medium" data-testid={`admin-poll-question-${report.poll_id}`}>
                          {report.poll_question || "Untitled poll"}
                        </div>
                      </td>
                      <td className="py-3 pr-4" data-testid={`admin-poll-creator-${report.poll_id}`}>
                        @{report.poll_creator_username}
                      </td>
                      <td className="py-3 pr-4" data-testid={`admin-poll-count-${report.poll_id}`}>
                        {report.report_count}
                      </td>
                      <td className="py-3 pr-4" data-testid={`admin-poll-latest-${report.poll_id}`}>
                        {report.latest_report_at}
                      </td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/poll/${report.poll_id}`)}
                            data-testid={`admin-view-${report.poll_id}`}
                          >
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "blocked" && (
        <>
          {loading ? (
            <div className="text-sm text-muted-foreground" data-testid="admin-blocked-loading">
              Loading blocked users...
            </div>
          ) : blockedUsers.length === 0 ? (
            <div
              className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground"
              data-testid="admin-blocked-empty"
            >
              No blocked users.
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-blocked-list">
              {blockedUsers.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between rounded-xl border border-border bg-card p-4"
                  data-testid={`admin-blocked-user-${u.id}`}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={u.avatar_url}
                      alt={u.username}
                      className="h-10 w-10 rounded-full object-cover"
                      data-testid={`admin-blocked-avatar-${u.id}`}
                    />
                    <div>
                      <div className="text-sm font-semibold" data-testid={`admin-blocked-username-${u.id}`}>
                        @{u.username}
                      </div>
                      <div className="text-xs text-muted-foreground" data-testid={`admin-blocked-email-${u.id}`}>
                        {u.email}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUnblock(u.id)}
                    data-testid={`admin-unblock-${u.id}`}
                  >
                    Unblock
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <AlertDialog open={!!bulkAction} onOpenChange={(open) => !open && setBulkAction(null)}>
        <AlertDialogContent data-testid="admin-bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="admin-bulk-confirm-title">
              {bulkAction === "delete" && `Delete ${selectedIds.length} poll(s)?`}
              {bulkAction === "ignore" && `Ignore reports for ${selectedIds.length} poll(s)?`}
              {bulkAction === "block" && `Block creator(s) of ${selectedIds.length} poll(s)?`}
            </AlertDialogTitle>
            <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing} data-testid="admin-bulk-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={executeBulk} disabled={processing} data-testid="admin-bulk-confirm-action">
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
